import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Spinner, Modal, ModalHeader, ModalBody, Input } from 'reactstrap';
import { fetchPaymentProfile, fetchCheckoutPaymentProfile, fetchMerchantSetupIntent, allowsNetTerms, getCandidateAuth0Ids, getStoredStoreInvoiceFrequency, normalizeInvoiceFrequency, deleteStoredPaymentMethod } from '../../services/paymentService';
import { getCompanyPaymentTermPolicy } from '../../services/paymentTerms';
import { FaCreditCard, FaExclamationCircle, FaPlus, FaTrashAlt } from 'react-icons/fa';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import AddCardForm from './AddCardForm';
import { UpgradeHint } from './featureLock';
import config, { resolveStripePublicKey } from '../../config';

const connectedAccountID = config.config.STRIPE.connectedAccountID;

const getNetTermsDescription = invoiceFrequency => {
    const normalized = normalizeInvoiceFrequency(invoiceFrequency);

    if (normalized === 'daily') {
        return 'Daily Invoicing';
    }

    if (normalized === 'weekly') {
        return 'Weekly Invoicing';
    }

    if (normalized === 'monthly' || normalized === 'net_30') {
        return 'Net 30 Invoicing';
    }

    if (normalized === 'net_14') {
        return 'Net 14 Invoicing';
    }

    if (normalized === 'net_60') {
        return 'Net 60 Invoicing';
    }

    if (normalized === 'net_120') {
        return 'Net 120 Invoicing';
    }

    return 'Net Terms Invoicing';
};

const stripeKey = resolveStripePublicKey();
const PAYMENT_PROFILE_LOAD_TIMEOUT_MS = 12000;
const PAYMENT_PROFILE_RETRY_DELAYS_MS = [1500, 4000, 9000];

const arePaymentProfileStatesEqual = (left = {}, right = {}) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
        return false;
    }

    return leftKeys.every(key => left[key] === right[key]);
};

const PaymentMethodDisplay = ({
    onError,
    shouldFetch = true,
    provisionMerchantProfile = true,
    currentUserProfileFallback = true,
    includeCustomerCards = true,
    adminStoreProfileLookup,
    storeProfileLookup = true,
    onPaymentMethodSelect,
    onPaymentProfileStateChange,
    onPaymentModeChange,
    selectedPaymentMode = 'net_terms',
    paymentProfileState = {},
    merchantAuth0Id = null,
    merchantAuth0Ids = [],
    merchantProfileSeed = null,
    storeId = '',
    cardAuth0Id = null,
    netTermsLocked = false
}) => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [invoiceFrequency, setInvoiceFrequency] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);
    const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
    const [isPreparingAddCardModal, setIsPreparingAddCardModal] = useState(false);
    const [stripePromise, setStripePromise] = useState(null);
    const [addCardSetupIntentPayload, setAddCardSetupIntentPayload] = useState(null);
    const [addCardError, setAddCardError] = useState(null);
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
    const [deletingPaymentMethodId, setDeletingPaymentMethodId] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const isMountedRef = useRef(true);
    const retryTimeoutIdsRef = useRef([]);
    const paymentProfileRetryIndexRef = useRef(0);
    const isPaymentProfileFetchInFlightRef = useRef(false);

    const resolvedMerchantAuth0Ids = getCandidateAuth0Ids({ auth0Id: merchantAuth0Id, auth0Ids: merchantAuth0Ids });
    const merchantAuth0IdsKey = resolvedMerchantAuth0Ids.join('|');
    const paymentProfileFetchKey = `${storeId || ''}|${includeCustomerCards ? (cardAuth0Id || '') : '__customer_cards_disabled__'}|${merchantAuth0IdsKey}`;
    const resolvedAddCardAuth0Id = paymentProfileState.netTermsBillingAuth0Id
        || paymentProfileState.billingAuth0Id
        || paymentProfileState.cardBillingAuth0Id
        || paymentProfileState.selectedCardBillingAuth0Id
        || cardAuth0Id
        || resolvedMerchantAuth0Ids[0]
        || merchantAuth0Id
        || null;
    const resolvedAddCardAuth0Ids = getCandidateAuth0Ids({
        auth0Ids: [
            paymentProfileState.netTermsBillingAuth0Id,
            paymentProfileState.billingAuth0Id,
            paymentProfileState.cardBillingAuth0Id,
            paymentProfileState.selectedCardBillingAuth0Id,
            cardAuth0Id,
            ...resolvedMerchantAuth0Ids,
            merchantAuth0Id,
            resolvedAddCardAuth0Id,
        ],
    });
    const setupIntentAuth0Id = resolvedAddCardAuth0Id || resolvedAddCardAuth0Ids[0] || null;
    const selectedPaymentMethod = paymentMethods.find(method => method.gateway_profile_id === selectedPaymentMethodId) || null;
    const resolvedDeleteCardAuth0Id = (selectedPaymentMethod && selectedPaymentMethod.billing_auth0_id) || paymentProfileState.cardBillingAuth0Id || resolvedAddCardAuth0Id;
    const basePaymentTermPolicy = useMemo(
        () => getCompanyPaymentTermPolicy({ paymentTerm: invoiceFrequency }),
        [invoiceFrequency]
    );
    // Free tier is prepay only — use the single/prepay policy so the order charges the card on claim.
    const paymentTermPolicy = useMemo(
        () => (netTermsLocked
            ? getCompanyPaymentTermPolicy({ paymentTerm: 'single' })
            : basePaymentTermPolicy),
        [basePaymentTermPolicy, netTermsLocked]
    );

    const getSetupIntentPayload = response => {
        if (response && response.payload && typeof response.payload === 'object') {
            return response.payload;
        }

        return response && typeof response === 'object' ? response : null;
    };

    const getSetupIntentPublishableKey = payload => {
        if (!payload || typeof payload !== 'object') {
            return stripeKey;
        }

        if (typeof payload.stripe_publishable_key === 'string' && payload.stripe_publishable_key) {
            return payload.stripe_publishable_key;
        }

        return stripeKey;
    };

    const buildPaymentProfileState = (overrides = {}) => ({
        ...paymentProfileState,
        ...overrides,
    });

    const getFallbackInvoiceFrequency = () => {
        const cachedStoreInvoiceFrequency = storeId ? getStoredStoreInvoiceFrequency(storeId) : null;
        const currentInvoiceFrequency = normalizeInvoiceFrequency(paymentProfileState.invoiceFrequency);

        if (!currentInvoiceFrequency || currentInvoiceFrequency === 'n/a') {
            return cachedStoreInvoiceFrequency || null;
        }

        if (!storeId || currentInvoiceFrequency !== 'single' || paymentProfileState.loaded) {
            return currentInvoiceFrequency;
        }

        return cachedStoreInvoiceFrequency || null;
    };

    const formatPaymentMethodLabel = method => {
        const last4 = method && method.payment_method_number ? method.payment_method_number.slice(-4) : '';
        const provider = method && method.payment_method_provider
            ? method.payment_method_provider.charAt(0).toUpperCase() + method.payment_method_provider.slice(1)
            : 'Card';

        return last4 ? `${provider} •••• ${last4}` : provider;
    };

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            retryTimeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
            retryTimeoutIdsRef.current = [];
        };
    }, []);

    useEffect(() => {
        // Only fetch when shouldFetch is true and we haven't fetched yet
        if (shouldFetch && !hasFetched) {
            loadPaymentMethods();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetch, hasFetched, paymentProfileFetchKey]);

    useEffect(() => {
        setHasFetched(false);
        setPaymentMethods([]);
        setInvoiceFrequency(null);
        setError(null);
        setSelectedPaymentMethodId(null);
        setDeleteError(null);
        paymentProfileRetryIndexRef.current = 0;
        retryTimeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
        retryTimeoutIdsRef.current = [];
    }, [paymentProfileFetchKey]);

    const schedulePaymentProfileRetry = () => {
        if (!isMountedRef.current || !shouldFetch) {
            return;
        }

        const retryIndex = paymentProfileRetryIndexRef.current;
        if (retryIndex >= PAYMENT_PROFILE_RETRY_DELAYS_MS.length) {
            return;
        }

        const delayMs = PAYMENT_PROFILE_RETRY_DELAYS_MS[retryIndex];
        paymentProfileRetryIndexRef.current = retryIndex + 1;

        const timeoutId = setTimeout(() => {
            loadPaymentMethods();
        }, delayMs);
        retryTimeoutIdsRef.current.push(timeoutId);
    };

    // Set selection after loading payment methods based on payment-term policy.
    useEffect(() => {
        if (!loading && hasFetched) {
            const hasSavedCards = paymentMethods.length > 0;
            const firstAvailablePaymentMethodId = hasSavedCards ? paymentMethods[0].gateway_profile_id : null;
            let defaultId = null;
            let nextPaymentMode = paymentTermPolicy.defaultSelectedPaymentMode;

            if (paymentTermPolicy.isPrepay) {
                if (hasSavedCards) {
                    defaultId = paymentMethods[0].gateway_profile_id;
                }
                nextPaymentMode = 'card';
            } else if (selectedPaymentMode === 'card' && hasSavedCards) {
                const selectedCard = paymentMethods.find(method => method.gateway_profile_id === selectedPaymentMethodId);
                defaultId = (selectedCard && selectedCard.gateway_profile_id) || paymentMethods[0].gateway_profile_id;
                nextPaymentMode = 'card';
            } else if (paymentTermPolicy.isGrouped) {
                defaultId = null;
                nextPaymentMode = 'company_terms';
            } else if (selectedPaymentMode === 'company_terms' && paymentTermPolicy.allowsCompanyTermsSelection) {
                defaultId = null;
                nextPaymentMode = 'company_terms';
            } else if (selectedPaymentMethodId && hasSavedCards) {
                const selectedCard = paymentMethods.find(method => method.gateway_profile_id === selectedPaymentMethodId);
                defaultId = selectedCard ? selectedCard.gateway_profile_id : paymentMethods[0].gateway_profile_id;
                nextPaymentMode = 'card';
            } else if (paymentTermPolicy.allowsCompanyTermsSelection && !paymentTermPolicy.paymentMethodRequired) {
                defaultId = null;
                nextPaymentMode = 'company_terms';
            } else if (hasSavedCards) {
                defaultId = paymentMethods[0].gateway_profile_id;
                nextPaymentMode = 'card';
            }

            setSelectedPaymentMethodId(defaultId);
            if (onPaymentMethodSelect) {
                onPaymentMethodSelect(defaultId);
            }

            const defaultPaymentMethod = paymentMethods.find(method => method.gateway_profile_id === defaultId) || null;
            updatePaymentProfileState(buildPaymentProfileState({
                defaultPaymentMethodId: firstAvailablePaymentMethodId,
                selectedPaymentMethodId: defaultId,
                selectedCardBillingAuth0Id: defaultPaymentMethod && defaultPaymentMethod.billing_auth0_id
                    ? defaultPaymentMethod.billing_auth0_id
                    : null,
            }));

            if (onPaymentModeChange) {
                onPaymentModeChange(nextPaymentMode);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, hasFetched, invoiceFrequency, paymentMethods, selectedPaymentMode, paymentTermPolicy]);

    const handleSelectPaymentMethod = paymentMethodId => {
        setDeleteError(null);
        setSelectedPaymentMethodId(paymentMethodId);
        const nextSelectedPaymentMethod = paymentMethods.find(method => method.gateway_profile_id === paymentMethodId) || null;
        updatePaymentProfileState(buildPaymentProfileState({
            defaultPaymentMethodId: paymentMethods.length > 0 ? paymentMethods[0].gateway_profile_id : null,
            selectedPaymentMethodId: paymentMethodId || null,
            selectedCardBillingAuth0Id: nextSelectedPaymentMethod && nextSelectedPaymentMethod.billing_auth0_id
                ? nextSelectedPaymentMethod.billing_auth0_id
                : null,
        }));
        if (onPaymentMethodSelect) {
            onPaymentMethodSelect(paymentMethodId);
        }
        if (onPaymentModeChange) {
            onPaymentModeChange(paymentMethodId ? 'card' : 'company_terms');
        }
    };

    const updatePaymentProfileState = (nextState) => {
        if (onPaymentProfileStateChange && !arePaymentProfileStatesEqual(paymentProfileState, nextState)) {
            onPaymentProfileStateChange(nextState);
        }
    };

    const loadPaymentMethods = async () => {
        if (!isMountedRef.current || isPaymentProfileFetchInFlightRef.current) {
            return;
        }

        isPaymentProfileFetchInFlightRef.current = true;
        setLoading(true);
        setDeleteError(null);
        setHasFetched(true);
        try {
            const storeInvoiceFrequencyOverride = storeId ? getStoredStoreInvoiceFrequency(storeId) : null;
            const paymentProfilePromise = storeId
                ? fetchCheckoutPaymentProfile({
                    storeId,
                    storeProfileLookup,
                    cardAuth0Id,
                    includeCustomerCards,
                    ...(typeof adminStoreProfileLookup === 'boolean' ? { admin: adminStoreProfileLookup } : {}),
                    merchantAuth0Id,
                    merchantAuth0Ids: resolvedMerchantAuth0Ids,
                    storeInvoiceFrequencyOverride,
                })
                : fetchPaymentProfile({
                    auth0Ids: resolvedMerchantAuth0Ids,
                    provisionMerchantProfile,
                    currentUserProfileFallback,
                });

            const profile = await Promise.race([
                paymentProfilePromise,
                new Promise((_, reject) => {
                    const timeoutId = setTimeout(() => {
                        clearTimeout(timeoutId);
                        reject(new Error('Payment profile lookup timed out'));
                    }, PAYMENT_PROFILE_LOAD_TIMEOUT_MS);
                })
            ]);

            // API returns { status, hasPayload, payload: { payment_methods: [...], invoice_frequency: "..." } }
            const fallbackInvoiceFrequency = getFallbackInvoiceFrequency();

            if (profile && profile.payload) {
                const paymentMethods = profile.payload.payment_methods || [];
                const nextInvoiceFrequency = profile.payload.invoice_frequency || fallbackInvoiceFrequency || null;
                const nextAllowsNetTerms = allowsNetTerms(nextInvoiceFrequency);
                const resolvedBillingAuth0Id = profile.resolved_auth0_id || profile.payload.resolved_auth0_id || resolvedMerchantAuth0Ids[0] || null;
                const resolvedCardBillingAuth0Id = profile.card_auth0_id || profile.payload.card_auth0_id || cardAuth0Id || null;

                if (!isMountedRef.current) {
                    return;
                }

                setPaymentMethods(paymentMethods);
                setInvoiceFrequency(nextInvoiceFrequency);
                setError(null);
                paymentProfileRetryIndexRef.current = 0;
                updatePaymentProfileState({
                    loaded: true,
                    fetchFailed: false,
                    allowsNetTerms: nextAllowsNetTerms,
                    hasSavedPaymentMethods: paymentMethods.length > 0,
                    invoiceFrequency: nextInvoiceFrequency,
                    billingAuth0Id: resolvedBillingAuth0Id,
                    netTermsBillingAuth0Id: resolvedBillingAuth0Id,
                    cardBillingAuth0Id: resolvedCardBillingAuth0Id,
                    defaultPaymentMethodId: paymentMethods.length > 0 ? paymentMethods[0].gateway_profile_id : null,
                    selectedPaymentMethodId: null,
                    selectedCardBillingAuth0Id: paymentMethods.length > 0 && paymentMethods[0].billing_auth0_id
                        ? paymentMethods[0].billing_auth0_id
                        : null
                });
            } else {
                if (!isMountedRef.current) {
                    return;
                }

                setPaymentMethods([]);
                setInvoiceFrequency(fallbackInvoiceFrequency);
                if (profile === null) {
                    setError('Unable to load payment methods');
                    if (onError) onError('payment_fetch_failed');
                    schedulePaymentProfileRetry();
                }
                updatePaymentProfileState({
                    loaded: true,
                    fetchFailed: profile === null,
                    allowsNetTerms: allowsNetTerms(fallbackInvoiceFrequency),
                    hasSavedPaymentMethods: false,
                    invoiceFrequency: fallbackInvoiceFrequency,
                    billingAuth0Id: null,
                    netTermsBillingAuth0Id: paymentProfileState.netTermsBillingAuth0Id || paymentProfileState.billingAuth0Id || null,
                    cardBillingAuth0Id: cardAuth0Id || null,
                    defaultPaymentMethodId: null,
                    selectedPaymentMethodId: null,
                    selectedCardBillingAuth0Id: null
                });
            }
        } catch (fetchError) {
            if (!isMountedRef.current) {
                return;
            }

            setPaymentMethods([]);
            const fallbackInvoiceFrequency = getFallbackInvoiceFrequency();
            setInvoiceFrequency(fallbackInvoiceFrequency);
            setError('Unable to load payment methods');
            if (onError) onError('payment_fetch_failed');
            schedulePaymentProfileRetry();
            updatePaymentProfileState({
                loaded: true,
                fetchFailed: true,
                allowsNetTerms: allowsNetTerms(fallbackInvoiceFrequency),
                hasSavedPaymentMethods: false,
                invoiceFrequency: fallbackInvoiceFrequency,
                billingAuth0Id: null,
                netTermsBillingAuth0Id: paymentProfileState.netTermsBillingAuth0Id || paymentProfileState.billingAuth0Id || null,
                cardBillingAuth0Id: cardAuth0Id || null,
                defaultPaymentMethodId: null,
                selectedPaymentMethodId: null,
                selectedCardBillingAuth0Id: null
            });
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }

            isPaymentProfileFetchInFlightRef.current = false;
        }
    };

    const handleCloseModal = () => {
        setIsAddCardModalOpen(false);
        setAddCardError(null);
    };

    const handleOpenAddCardModal = async () => {
        if (isPreparingAddCardModal) {
            return;
        }

        setIsPreparingAddCardModal(true);
        setAddCardError(null);

        if (!stripeKey) {
            setAddCardError('Stripe card setup is unavailable in this environment. Use a test publishable key for local HTTP development.');
            setIsPreparingAddCardModal(false);
            return;
        }

        setAddCardSetupIntentPayload(null);
        setStripePromise(loadStripe(stripeKey, { stripeAccount: connectedAccountID }));
        setIsAddCardModalOpen(true);

        try {
            const setupIntentResponse = await fetchMerchantSetupIntent({
                auth0Id: setupIntentAuth0Id,
                auth0Ids: setupIntentAuth0Id ? [setupIntentAuth0Id] : resolvedAddCardAuth0Ids,
                merchantProfileSeed,
                // Add-card setup should be merchant-scoped, not store-profile-scoped.
                // This avoids fragile store-payment-profile setup-intent proxy failures.
                storeId: '',
                preferUsersProxy: false,
                provisionMerchantProfile,
            });
            const setupIntentPayload = getSetupIntentPayload(setupIntentResponse);
            const setupIntentStripeKey = getSetupIntentPublishableKey(setupIntentPayload);

            if (!setupIntentStripeKey) {
                setAddCardError('Stripe is not available for card setup in the current environment.');
                return;
            }

            setAddCardSetupIntentPayload(setupIntentPayload);
            setStripePromise(loadStripe(setupIntentStripeKey, { stripeAccount: connectedAccountID }));
        } catch (modalError) {
            const errorMessage = modalError && modalError.message
                ? modalError.message
                : 'Unable to initialize card setup';
            setAddCardError(errorMessage);
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setIsPreparingAddCardModal(false);
        }
    };

    const handleCardAdded = () => {
        setIsAddCardModalOpen(false);
        if (onPaymentModeChange) {
            onPaymentModeChange('card');
        }
        // Reload payment methods after successfully adding a card.
        // Multiple attempts account for eventual consistency between Stripe and profile APIs.
        [1000, 2500, 6000].forEach(delayMs => {
            const timeoutId = setTimeout(() => {
                loadPaymentMethods();
            }, delayMs);
            retryTimeoutIdsRef.current.push(timeoutId);
        });
    };

    const handleDeleteSelectedPaymentMethod = async () => {
        if (!selectedPaymentMethodId || !resolvedDeleteCardAuth0Id) {
            return;
        }

        const confirmed = window.confirm('Delete this card from the checkout profile?');
        if (!confirmed) {
            return;
        }

        setDeletingPaymentMethodId(selectedPaymentMethodId);
        setDeleteError(null);

        try {
            await deleteStoredPaymentMethod({
                auth0Id: resolvedDeleteCardAuth0Id,
                paymentMethodId: selectedPaymentMethodId,
            });

            const remainingPaymentMethods = paymentMethods.filter(method => method.gateway_profile_id !== selectedPaymentMethodId);
            const nextPaymentMethodId = remainingPaymentMethods.length > 0
                ? remainingPaymentMethods[0].gateway_profile_id
                : null;

            setPaymentMethods(remainingPaymentMethods);
            handleSelectPaymentMethod(nextPaymentMethodId);

            [250, 1200].forEach(delayMs => {
                const timeoutId = setTimeout(() => {
                    loadPaymentMethods();
                }, delayMs);
                retryTimeoutIdsRef.current.push(timeoutId);
            });
        } catch (deleteMethodError) {
            setDeleteError(deleteMethodError.message || 'Unable to delete payment method');
        } finally {
            setDeletingPaymentMethodId(null);
        }
    };

    return (
        <div>
            <div style={{ color: '#fff', fontStyle: 'bold', fontSize: '14px', marginBottom: '15px' }}>Payment Method(s)</div>

            {loading ? (
                <div style={{ marginLeft: '20px', color: '#cdcaca' }}>
                    <Spinner size="sm" /> Loading payment methods...
                </div>
            ) : error ? (
                <div style={{ marginLeft: '20px', color: '#cdcaca' }}>
                    <FaExclamationCircle style={{ marginRight: '5px' }} />
                    {error}
                    <div style={{ marginTop: '10px' }}>
                        <Button
                            color="default"
                            size="sm"
                            onClick={() => {
                                paymentProfileRetryIndexRef.current = 0;
                                loadPaymentMethods();
                            }}
                        >
                            Retry Loading Payment Methods
                        </Button>
                    </div>
                </div>
            ) : paymentMethods.length === 0 && !allowsNetTerms(invoiceFrequency) ? (
                <div style={{ marginLeft: '20px', color: '#cdcaca' }}>No payment method yet</div>
            ) : (
                <div style={{ marginLeft: '20px' }}>
                    <div style={{ color: '#cdcaca', fontSize: '12px', marginBottom: '12px' }}>
                        <strong style={{ color: '#fff' }}>{paymentTermPolicy.label}</strong> • {paymentTermPolicy.description}
                    </div>

                    {netTermsLocked ? (
                        <UpgradeHint
                            label="Upgrade to Pro for net terms / invoice billing — Free is prepay only"
                            style={{ marginTop: 0, marginBottom: 12 }}
                        />
                    ) : null}

                    <Input
                        type="select"
                        value={selectedPaymentMethodId || ''}
                        onChange={event => handleSelectPaymentMethod(event.target.value || null)}
                        style={{
                            backgroundColor: '#1f2133',
                            color: '#fff',
                            border: '1px solid #344675',
                            borderRadius: '8px',
                            minHeight: '46px',
                            marginBottom: '12px'
                        }}
                    >
                        {paymentTermPolicy.allowsCompanyTermsSelection ? (
                            <option value="">{paymentTermPolicy.label} • {getNetTermsDescription(invoiceFrequency)}</option>
                        ) : null}
                        {paymentMethods.map(method => (
                            <option key={method.gateway_profile_id} value={method.gateway_profile_id}>
                                {formatPaymentMethodLabel(method)}
                            </option>
                        ))}
                    </Input>

                    {selectedPaymentMethod ? (
                        <div style={{ backgroundColor: '#1f2133', border: '1px solid #344675', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <FaCreditCard style={{ marginRight: '12px', color: '#f7941d', fontSize: '18px' }} />
                                    <div>
                                        <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px' }}>{formatPaymentMethodLabel(selectedPaymentMethod)}</div>
                                        {selectedPaymentMethod.payment_method_exp_date ? (
                                            <div style={{ color: '#cdcaca', fontSize: '12px', marginTop: '2px' }}>
                                                Expires {selectedPaymentMethod.payment_method_exp_date}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    disabled={deletingPaymentMethodId === selectedPaymentMethodId}
                                    onClick={handleDeleteSelectedPaymentMethod}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
                                >
                                    {deletingPaymentMethodId === selectedPaymentMethodId ? <Spinner size="sm" /> : <FaTrashAlt />}
                                    Delete
                                </Button>
                            </div>
                        </div>
                    ) : null}

                    {deleteError ? (
                        <div style={{ color: '#ff8f8f', fontSize: '12px', marginBottom: '10px' }}>
                            <FaExclamationCircle style={{ marginRight: '6px' }} />
                            {deleteError}
                        </div>
                    ) : null}
                </div>
            )}

            {/* Add New Card Button */}
            <div style={{ marginTop: '15px', marginLeft: '20px' }}>
                <Button color="primary" size="sm" outline disabled={isPreparingAddCardModal} onClick={handleOpenAddCardModal} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaPlus /> {isPreparingAddCardModal ? 'Preparing...' : 'Add New Card'}
                </Button>
                {addCardError ? (
                    <div style={{ color: '#ff8f8f', fontSize: '12px', marginTop: '8px' }}>
                        {addCardError}
                    </div>
                ) : null}
                {!stripeKey ? (
                    <div style={{ color: '#ff8f8f', fontSize: '12px', marginTop: '8px' }}>
                        Stripe card setup is unavailable in this environment. Use a test publishable key for local HTTP development.
                    </div>
                ) : null}
            </div>
            {/* === NET TERMS PLACEHOLDER - Comment out to remove === */}
            {/* <div style={{ marginTop: '20px', marginLeft: '20px' }}>
                <Button color="info" size="sm" outline disabled style={{ cursor: 'not-allowed' }}>
                    Apply for Net Terms
                </Button>
                <div style={{ color: '#6c757d', fontSize: '11px', marginTop: '5px' }}>Coming soon</div>
            </div> */}
            {/* === END NET TERMS PLACEHOLDER === */}
            {/* Add Card Modal */}
            <Modal isOpen={isAddCardModalOpen} toggle={handleCloseModal} centered className="add-card-modal-wide">
                <ModalHeader toggle={handleCloseModal} style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '14px 18px' }}>
                    <span style={{ color: '#212529', fontSize: '16px', fontWeight: 'bold' }}>Add New Card</span>
                </ModalHeader>
                <ModalBody style={{ backgroundColor: '#ffffff', padding: '20px' }}>
                    {stripePromise ? (
                        <Elements stripe={stripePromise}>
                            <AddCardForm onSuccess={handleCardAdded} merchantAuth0Id={resolvedAddCardAuth0Id} merchantAuth0Ids={resolvedAddCardAuth0Ids} merchantProfileSeed={merchantProfileSeed} storeId={storeId} adminStoreProfileLookup={adminStoreProfileLookup} initialSetupIntentPayload={addCardSetupIntentPayload} provisionMerchantProfile={provisionMerchantProfile} />
                        </Elements>
                    ) : isPreparingAddCardModal ? (
                        <div style={{ color: '#495057', fontSize: '14px' }}>
                            Initializing Stripe...
                        </div>
                    ) : (
                        <div style={{ color: '#495057', fontSize: '14px' }}>
                            {addCardError || 'Stripe is not available for card setup in the current environment.'}
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default PaymentMethodDisplay;
